import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Chip, Link, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import Loader from "../../../UiComponents/Loader/Loader";
import { getRoadzenCompanyWalletLedger } from "../../../Service/RoadzenService";
import { rupees, whenText } from "../../Policy/BriskWallet/briskWalletUtils";
import { statusColor } from "../RoadzenCertificate/roadzenCertUtils";

const statusColorOf = (row) => (row.status === "ADDED" ? "green" : row.status === "REMOVED" ? "red" : statusColor(row.status));

/**
 * The Company Wallet "RoadZen" ledger, newest first, paged by the server (a copy of the Brisk Wallet's BriskLedgerTable
 * with RoadZen's columns). Each row is one wallet movement with the RoadZen certificate it was for and the balance the
 * wallet stored right after it. An Online Policy's certificate links to the policy.
 *
 * `query` = { clientId } for the super admin's one agency; `onLoaded(data)` gets the totals.
 */
const RoadzenCompanyLedgerTable = ({ query, showAgency = false, refreshKey = 0, onLoaded }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState("");
  const loadedRef = useRef(onLoaded);
  loadedRef.current = onLoaded;
  const queryKey = JSON.stringify(query || {});

  useEffect(() => setPage(0), [queryKey]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getRoadzenCompanyWalletLedger({ ...(query || {}), page: page + 1, limit }).then((res) => {
      if (!alive) return;
      setLoading(false);
      if (res.error) {
        setProblem(res.message || "The Zen Wallet could not be read");
        setRows([]);
        setTotal(0);
        return;
      }
      setProblem("");
      setRows(
        (res.data.rows || []).map((r) => ({
          ...r,
          dateText: whenText(r.at),
          userText: r.userName || "-",
          customerText: r.customerName || "-",
          chassisText: r.chassisNo || "-",
          productText: r.productCode || "-",
          totalText: r.totalPremium ? rupees(r.totalPremium) : "-",
          netText: r.netPremium ? rupees(r.netPremium) : "-",
          creditText: r.credit ? rupees(r.credit) : "",
          debitText: r.debit ? rupees(r.debit) : "",
          balanceText: rupees(r.balanceAfter),
          statusText: r.statusText || "-",
        }))
      );
      setTotal(res.data.total || 0);
      if (loadedRef.current) loadedRef.current(res.data);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, page, limit, refreshKey]);

  const certificateCell = (row) => (
    <Box sx={{ minWidth: 120 }}>
      <Typography sx={{ fontSize: 13 }}>{row.certificateNo || (row.onlinePolicyId ? "" : "-")}</Typography>
      {row.onlinePolicyId && (
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate(`/onlinePolicy/view/form/${row.onlinePolicyId}`)}
          sx={{ fontSize: 12, textAlign: "left" }}
        >
          Policy {row.policyNo || "(open)"}
        </Link>
      )}
    </Box>
  );

  const columns = [
    { id: "dateText", label: "Transaction Date", placeMent: true, disablePadding: false },
    ...(showAgency ? [{ id: "agencyName", label: "Agency", placeMent: true, disablePadding: false }] : []),
    { id: "typeLabel", label: "Transaction Type", placeMent: true, disablePadding: false },
    { id: "userText", label: "User Name", placeMent: true, disablePadding: false },
    { id: "certificateCell", label: "Certificate Number", placeMent: false, disablePadding: false, type: "component", renderComponent: certificateCell },
    { id: "customerText", label: "Customer Name", placeMent: true, disablePadding: false },
    { id: "chassisText", label: "Chassis No", placeMent: true, disablePadding: false },
    { id: "productText", label: "Product", placeMent: true, disablePadding: false },
    { id: "totalText", label: "Total Premium", placeMent: true, disablePadding: false },
    { id: "netText", label: "Net Premium", placeMent: true, disablePadding: false },
    { id: "creditText", label: "Credit", placeMent: true, disablePadding: false, type: "coloredText", getColor: () => "green" },
    { id: "debitText", label: "Debit", placeMent: true, disablePadding: false, type: "coloredText", getColor: () => "red" },
    { id: "balanceText", label: "Balance", placeMent: true, disablePadding: false },
    { id: "statusText", label: "Status", placeMent: true, disablePadding: false, type: "coloredText", getColor: statusColorOf },
    {
      id: "apiEnv",
      label: "RoadZen",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) =>
        row.apiEnv ? <Chip size="small" label={row.apiEnv} color={row.apiEnv === "TEST" ? "warning" : "success"} /> : <span>-</span>,
    },
  ];

  return (
    <>
      {loading && <Loader />}
      {problem && (
        <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
          {problem}
        </Alert>
      )}
      <MasterCommonTable
        data={rows}
        tableTotalCount={total}
        columns={columns}
        isServerSide={true}
        limit={limit}
        paginationReset={queryKey}
        rowsPerPageOptions={[10, 25, 50, 100]}
        tableOutputEvent={(event) => {
          if (event.page !== undefined) setPage(event.page - 1);
          if (event.limit) setLimit(event.limit);
        }}
        tableConfig={{ isAction: false, isSearch: false, isView: false, isEdit: false, isDelete: false, isMultiDelete: false, isDownload: false }}
      />
    </>
  );
};

export default RoadzenCompanyLedgerTable;
